<template>
  <v-dialog v-model="openEntityDialog" max-width="320px" :scroll-strategy="'none'">
    <template #activator="{ props }">
      <div v-if="!store.state.isWorkflowDisabled" v-bind="props" class="d-flex">
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" size="16" color="grey" class="cursor-pointer">mdi-pencil</v-icon>
          </template>
          Edit entity
        </v-tooltip>
      </div>
    </template>
    <v-card :key="refreshKey">
      <v-card-item class="pa-0">
        <span class='edit-entity-title'>Edit Entity</span>
        <v-btn
          icon="mdi-close"
          variant="plain"
          class="ml-auto"
          @click="openEntityDialog = false"
        />
      </v-card-item>
      <v-card-text class="entity-card" :style="{ 'border-color': cardTypeColor(entityCardType) }" ref="sortableList">
        <v-combobox
          density="compact"
          :model-value="entity"
          :style="{ border: '1px solid ' + cardTypeColor(entityCardType), marginBottom: '8px' }"
          disabled
        >
          <template #selection>
            <v-chip density="compact" class="mr-2">
              {{ entity.name }}
            </v-chip>
          </template>
        </v-combobox>
        <div
          v-for="(field, index) in entity.dataFields"
          :key="index"
          class="field-name d-flex align-center position-relative sortableRow"
        >
          <v-tooltip location="bottom" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="field.primaryKey || field.reference ? props : undefined"
                class="border-0 cursor-grab sortHandle"
                size="small"
                variant="plain"
                tabindex="-1"
                :icon="field.primaryKey ? 'mdi-key' : field.reference ? 'mdi-link' : 'mds:drag-indicator'"
                :disabled='store.state.isWorkflowDisabled'
              />
            </template>
            <span>{{ field.primaryKey ? 'Primary key' : 'Reference to entity' }}</span>
          </v-tooltip>
          <quick-rename-input
            :id="`edit-entity-${entity.id}-field-${index}`"
            class="flex-grow-1"
            :value="field.name"
            :density="`compact`"
            :max-length="30"
            @onChange="blurTableCell($event, entityIndex, index, 'name', null)"
            :disabled="store.state.isWorkflowDisabled"
          />
          <v-chip
            v-if="field.relatedEntityId && getRelatedEntity(field)"
            density="compact"
            size="small"
            class="px-2 ml-1"
          >
            {{ field.cardinality === 'one-to-many'
              ? `[ ${getRelatedEntity(field)?.name} ]`
              : `${getRelatedEntity(field)?.name}` }}
          </v-chip>
          <v-menu class="card-menu">
            <template v-slot:activator="{ props }">
              <v-btn
                icon="mdi-dots-vertical"
                size="small"
                variant="plain"
                v-bind="props"
              />
            </template>
            <v-list density="compact">
              <select-entity
                @onSelectEntity="(selectedEntity) => selectRelatedEntity(selectedEntity, entityIndex, index)"
              >
                <v-list-item class="cursor-pointer">
                  <v-list-item-title>
                    <v-icon class="mr-2" color="grey">mdi-database</v-icon> Select Entity
                  </v-list-item-title>
                </v-list-item>
              </select-entity>
              <v-list-item @click="setPrimaryKey(entityIndex, index)">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" :icon="field.primaryKey ? 'mdi-key-remove' : 'mdi-key'" />
                  {{ field.primaryKey ? 'Unset primary key' : 'Set as primary key' }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="setReference(entityIndex, index)">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" :icon="field.reference ? 'mdi-link-off' : 'mdi-link'" />
                  {{ field.reference ? 'Unset reference' : 'Set as reference' }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="deleteEntityField(entityIndex, index)">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
                </v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <div class="d-flex justify-center pt-1">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <toggle
                      v-bind="!field.relatedEntityId ? props : undefined"
                      mandatory
                      :value="field.cardinality || 'one-to-one'"
                      :options="[
                        { value: 'one-to-one', label: '1 : 1' },
                        { value: 'one-to-many', label: '1 : N' }
                      ]"
                      :disabled="store.state.isWorkflowDisabled || !field.relatedEntityId"
                      @update:modelValue="(cardinality) => selectCardinality(cardinality, entityIndex, field.relatedEntityId)"
                    />
                  </template>
                  <span>Disabled because this field is not set to be a related entity.</span>
                </v-tooltip>
              </div>
            </v-list>
          </v-menu>
        </div>
        <v-btn
          icon="mdi-plus"
          size="small"
          variant="plain"
          class="ml-auto"
          @click="addColumn(entityIndex)"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import useEntityActions from '@/composables/useEntityActions'
import useDomainModel from '@/composables/useDomainModel'
import Sortable from 'sortablejs'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import workflowApi from '@/api/workflowApi'

const { entity, refreshKey } = defineProps({ entity: Object, refreshKey: Number })

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const openEntityDialog = ref(false)
const entityIndex = computed(() => (workflow.value.dataModels ?? []).findIndex((item) => item.id === entity.id))
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const entityCardType = computed(() => diagramHelpers.methods.getAggregateCardType(requirementTypes.value))

const {
  blurTableCell,
  setPrimaryKey,
  setReference,
  deleteEntityField,
  addColumn,
  selectRelatedEntity,
  selectCardinality
} = useEntityActions()

const { getRelatedEntity, cardTypeColor } = useDomainModel()

const sortableList = ref(null)
let sortable = null

const onEndDrag = async (dropEvent) => {
  const { oldDraggableIndex: fromIndex, newDraggableIndex: toIndex } = dropEvent
  const newDataModels = structuredClone(workflow.value.dataModels || [])

  const field = newDataModels[entityIndex.value].dataFields[fromIndex]
  newDataModels[entityIndex.value].dataFields.splice(fromIndex, 1)
  newDataModels[entityIndex.value].dataFields.splice(toIndex, 0, field)

  await workflowApi.updateDataModels({
    dataModelsOnWorkflow: newDataModels,
    workflow: workflow.value
  })
}

const initSortable = () => {
  if (!sortableList.value) return

  sortable = new Sortable(sortableList.value.$el, {
    draggable: '.sortableRow',
    handle: '.sortHandle',
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    animation: 150,
    scroll: true,
    forceFallback: false,
    scrollSensitivity: 100,
    onEnd: onEndDrag,
    onStart: function (event) {
      Array.prototype.forEach.call(
        document.querySelectorAll('.v-row-group__header'),
        function (el, index) {
          if (!el.classList.contains('sortableRow') && index !== 0) {
            el.classList.add('sortableRow')
          }
        }
      )
      Array.prototype.forEach.call(
        document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
        function (el, index) {
          if (!el.classList.contains('white-background-on-hover') && index !== 0) {
            el.classList.add('white-background-on-hover')
          }
        }
      )
    }
  })
}

const destroySortable = () => {
  if (sortable) {
    sortable.destroy()
    sortable = null
  }
}

watch(
  [openEntityDialog, () => refreshKey],
  async ([dialogOpen]) => {
    if (dialogOpen) {
      await nextTick()
      destroySortable()
      initSortable()
    } else {
      destroySortable()
    }
  }
)
</script>

<style scoped>
.v-card {
  border: 1px solid var(--dark-2);
}

.card-menu .v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

:deep(.v-card-item__content) {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 10px;

  .edit-entity-title {
    font-size: 20px;
    color: var(--text-light) !important;
  }
}

.entity-card {
  min-width: 190px;
  max-width: 250px;
  margin: 12px auto 24px;
  padding: 12px !important;
  border-radius: 8px;
  background: #fff;
  border: 1px solid black;
  border-top-width: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
